import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import Icon from '../components/ui/icon';
import { useAuth } from '../contexts/AuthContext';
import { generateOutletAvatar, getAvatarStyleForOutletType } from '../lib/avatar';
import { 
  getOutletTypeText, 
  getOperatingHoursText, 
  getStoreSizeText, 
  getCustomerVolumeText,
  getTierNameInVietnamese
} from '../lib/outlet-utils';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuth();
  
  // Load real data from authenticated user (only show what we have)
  const user = authUser ? {
    name: authUser.name,
    outletId: authUser.outlet_id,
    outletType: authUser.outlet_type,
    tier: authUser.scoring.score_tier,
    score: authUser.scoring.overall_score,
    phone: authUser.phone,
    region: authUser.region,
    cityTier: authUser.city_tier,
    avatar: authUser.avatar || generateOutletAvatar(
      authUser.outlet_id,
      getAvatarStyleForOutletType(authUser.outlet_type)
    ),
    // Additional outlet info
    storeSize: authUser.store_size,
    customerVolume: authUser.customer_volume,
    operatingHours: authUser.operating_hours,
    customerType: authUser.customer_type,
    contactPerson: authUser.contact_person,
    // Scoring details
    erfmScore: authUser.scoring.erfm_score,
    missionScore: authUser.scoring.mission_score,
    orderScore: authUser.scoring.order_score,
    performanceScore: authUser.scoring.performance_score
  } : null;

  const menuItems = [
    { icon: 'bell', title: 'Thông báo', description: 'Quản lý thông báo', action: () => {} },
    { icon: 'settings', title: 'Cài đặt', description: 'Tùy chỉnh ứng dụng', action: () => {} },
    { icon: 'lock', title: 'Bảo mật', description: 'Đổi mật khẩu, 2FA', action: () => {} },
    { icon: 'help-circle', title: 'Trợ giúp', description: 'FAQ, liên hệ hỗ trợ', action: () => {} },
  ];

  const handleLogout = async () => {
    try {
      // Call logout function from AuthContext
      await logout();
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, still navigate to login
      navigate('/login');
    }
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authUser) {
      navigate('/login');
    }
  }, [authUser, navigate]);

  // Show loading if not authenticated
  if (!authUser) {
    return (
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <h1 className="text-2xl font-bold">Thông tin cửa hàng</h1>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Đang chuyển hướng...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no user data
  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <h1 className="text-2xl font-bold">Thông tin cửa hàng</h1>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <Icon name="alert-circle" size={48} />
            </div>
            <h2 className="text-lg font-semibold mb-2">Không thể tải thông tin</h2>
            <p className="text-muted-foreground mb-4">Vui lòng đăng nhập lại để xem thông tin cá nhân</p>
            <Button onClick={() => navigate('/login')}>
              Đăng nhập lại
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6 pb-20">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-3">🥇 Cửa hàng</h1>
        <p className="text-md text-muted-foreground px-4">Quản lý thông tin cửa hàng của bạn</p>
      </div>
      {/* User Profile Card */}
      <Card>
        <CardContent className="px-6 py-8 bg-primary rounded-lg">
          <div className="flex items-center justify-center gap-6">
            <Avatar className="h-16 w-16 self-start rounded-lg bg-secondary ">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="text-xl bg-primary-foreground text-primary">
                {user.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-3 text-sm uppercase text-white/60">
                {user.outletId} • {getOutletTypeText(user.outletType)} • {user.region}
              </div>
              <h2 className="text-2xl font-bold text-white">{user.name}</h2>
              <div className="flex items-end justify-start gap-2">
                <span className="text-sm text-white/80 uppercase">{user.score} điểm • hạng {getTierNameInVietnamese(user.tier)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardContent className="p-8">
          <h3 className="font-bold mb-6 text-xl">Thông tin liên hệ</h3>
          <div className="space-y-4 text-md">
            {user.phone && (
              <div className="flex items-center gap-2">
                <Icon name="phone" size={20} className="text-muted-foreground" />
                <span className="text-muted-foreground">Điện thoại: </span>
                <span className="text-md">{user.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Icon name="map-pin" size={20} className="text-muted-foreground" />
              <span className="text-muted-foreground">Khu vực: </span>
              <span className="text-md">{user.region} - {user.cityTier}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="user" size={20} className="text-muted-foreground" />
              <span className="text-muted-foreground">Người liên hệ: </span>
              <span className="text-md">{user.contactPerson}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outlet Details */}
      <Card>
        <CardContent className="p-8">
          <h3 className="font-bold mb-6 text-xl">Thông tin cửa hàng</h3>
          <div className="text-md space-y-4">
            <div className="flex items-center gap-2">
              <Icon name="shopping-cart" size={20} className="text-muted-foreground" />
              <span className="text-muted-foreground">Loại cửa hàng:</span>
              <span className="text-md">{getOutletTypeText(user.outletType)}</span>
            </div>
            {user.storeSize && (
              <div className="flex items-center gap-2">
                <Icon name="truck" size={20} className="text-muted-foreground" />
                <span className="text-muted-foreground">Quy mô:</span>
                <span className="text-md">{getStoreSizeText(user.storeSize)}</span>
              </div>
            )}
            {user.customerVolume && (
              <div className="flex items-center gap-2">
                <Icon name="user" size={20} className="text-muted-foreground" />
                <span className="text-muted-foreground">Lượng khách hàng:</span>
                <span className="text-md">
                  {getCustomerVolumeText(user.customerVolume)}
                </span>
              </div>
            )}
            {user.operatingHours && (
              <div className="flex items-center gap-2">
                <Icon name="clock" size={20} className="text-muted-foreground" />
                <span className="text-muted-foreground">Giờ hoạt động:</span>
                <span className="text-md">{getOperatingHoursText(user.operatingHours)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Menu Items */}
      <div className="space-y-4">
        {menuItems.map((item, index) => (
          <Card 
            key={index} 
            className={`cursor-pointer hover:shadow-md transition-shadow `}
          >
            <CardContent className="py-4 px-6">
              <div className="flex items-center gap-6">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-muted`}>
                  <Icon 
                    name={item.icon} 
                    size={24}
                  /> 
                </div>
                <div className="flex-1">
                  <h3 className={`font-bold text-md`}>
                    {item.title}
                  </h3>
                  <p className="text-md text-muted-foreground">{item.description}</p>
                </div>
                <Icon name="chevron-right" size={20} className="text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Logout Button - Prominent */}
      <div>
        <Button 
          variant="secondary" 
          className="w-full rounded-full text-md"
          size="lg"
          onClick={handleLogout}
        >
          <Icon name="logout" size={20} />
          Đăng xuất
        </Button>
      </div>
    </div>
  );
};

export default Profile;
